import { useCallback } from "react";
import type { AgentTemplate } from "@/api/agents";
import {
  getModelEffortConfig,
  type RuntimeKey,
  type EffortValue,
  type EffortChoice,
} from "@/lib/model-registry";
import { updateAgentTemplateConfig } from "@/lib/update-agent-template-config";
import {
  prepareInputBarNewChat,
  prepareModelConversation,
} from "@/lib/model-switch-conversation";
import { prewarmAgentSession } from "@/lib/agent-prewarm";
import { useAgentStore } from "@/stores/agent-store";

export interface InputModelPickOptions {
  /** Save the template only; the current chat routing stays as it is. */
  startNewConversation?: boolean;
  conversationFrameId?: string;
  /** Pick from the global input bar: a targeted old chat gives way to a pending new chat. */
  inputBarChat?: boolean;
}

export interface InputModelChoice {
  provider: RuntimeKey;
  runtimeId: string;
  model: string;
}

export interface InputEffortPickOptions {
  inputBarChat?: boolean;
  /** Model the effort belongs to, saved in the same template update. */
  runtime?: InputModelChoice;
}

function withModelChoice(
  config: AgentTemplate["adapter_config"],
  { provider, runtimeId, model }: InputModelChoice,
): Record<string, unknown> {
  const nextConfig: Record<string, unknown> = {
    ...config,
    model,
    model_policy: "pinned",
  };
  const [, runtimeSource] = runtimeId.split(":", 2);
  if (provider === "opencode_cli" && runtimeSource) {
    nextConfig.runtime_source = runtimeSource;
  } else {
    delete nextConfig.runtime_source;
  }
  // Sanitize thinking_effort against the new model's vocabulary so the
  // backend validator never sees a stale/sentinel value (e.g. "auto"
  // left over from an earlier UX, or an Opus-only "xhigh" being moved
  // to a Sonnet agent). If the new model has no effort knob, drop the
  // key; otherwise, keep the stored value only when still valid, else
  // fall back to the model's canonical default.
  // A replaced effort is no longer the user's choice, so its pin goes too.
  const effortCfg = getModelEffortConfig(model);
  const stored = nextConfig.thinking_effort;
  if (effortCfg.values.length === 0) {
    delete nextConfig.thinking_effort;
    delete nextConfig.effort_policy;
  } else if (
    typeof stored !== "string" ||
    !effortCfg.values.includes(stored as EffortValue)
  ) {
    delete nextConfig.effort_policy;
    if (effortCfg.default) {
      nextConfig.thinking_effort = effortCfg.default;
    } else {
      delete nextConfig.thinking_effort;
    }
  }
  return nextConfig;
}

export function useInputModelActions(
  projectIdForAgents: string | undefined,
  resolveTemplateForAgent: (agentId: string) => Promise<AgentTemplate | null>,
) {
  const handleUpdateAgentModel = useCallback(
    async (
      agentId: string,
      provider: RuntimeKey,
      runtimeId: string,
      model: string,
      options?: InputModelPickOptions,
    ) => {
      const finishSwitch = options?.inputBarChat
        ? prepareInputBarNewChat()
        : options?.startNewConversation === false
          ? null
          : prepareModelConversation(agentId, options?.conversationFrameId);
      const template = await resolveTemplateForAgent(agentId);
      if (!template) return;
      await updateAgentTemplateConfig(template.id, (template) => ({
        adapter_type: provider,
        adapter_config: withModelChoice(template.adapter_config, {
          provider,
          runtimeId,
          model,
        }),
      }));
      await useAgentStore.getState().fetchAllTemplates();
      if (projectIdForAgents) {
        await useAgentStore.getState().fetchProjectAgents(projectIdForAgents);
      }
      if (finishSwitch) await finishSwitch();
    },
    [projectIdForAgents, resolveTemplateForAgent],
  );

  const handleUpdateAgentEffort = useCallback(
    async (
      agentId: string,
      effort: EffortChoice,
      options?: InputEffortPickOptions,
    ) => {
      // Record the targeted chat before any save starts.
      const finishSwitch = options?.inputBarChat
        ? prepareInputBarNewChat()
        : null;
      const template = await resolveTemplateForAgent(agentId);
      if (!template) return;
      const runtime = options?.runtime;
      await updateAgentTemplateConfig(template.id, (template) => {
        const nextConfig: Record<string, unknown> = runtime
          ? withModelChoice(template.adapter_config, runtime)
          : { ...template.adapter_config };
        // "default" is a UX sentinel — resolve it to the model's canonical
        // working value so we always persist a real provider-accepted effort
        // (never "default" / "auto"). If the model has no effort support,
        // drop the key entirely. Only an explicit pick pins the effort.
        if (effort === "default") {
          const modelId = nextConfig.model as string | undefined;
          const cfg = getModelEffortConfig(modelId);
          if (cfg.default) {
            nextConfig.thinking_effort = cfg.default;
          } else {
            delete nextConfig.thinking_effort;
          }
          delete nextConfig.effort_policy;
        } else {
          nextConfig.thinking_effort = effort;
          nextConfig.effort_policy = "pinned";
        }
        return runtime
          ? { adapter_type: runtime.provider, adapter_config: nextConfig }
          : { adapter_config: nextConfig };
      });
      await useAgentStore.getState().fetchAllTemplates();
      if (projectIdForAgents) {
        await useAgentStore.getState().fetchProjectAgents(projectIdForAgents);
      }
      prewarmAgentSession("effort_changed", { agentId, force: true });
      finishSwitch?.();
    },
    [projectIdForAgents, resolveTemplateForAgent],
  );

  return { handleUpdateAgentModel, handleUpdateAgentEffort };
}
